import algoliasearch, { type SearchIndex } from "algoliasearch";
import type { FacilityHit, GlobalHit, PersonHit } from "@/components/AlgoliaSearch";
import { formatPhoneNumber, normalizePhoneNumber, normalizePhoneSearchQuery } from "@/lib/phoneHelpers";
import { formatPhoneQueryWithDashes, hitHasPhoneNumber } from "./phoneUtils";

const appId = import.meta.env.VITE_ALGOLIA_APP_ID;
const apiKey = import.meta.env.VITE_ALGOLIA_API_KEY;
const indexName = import.meta.env.VITE_GLOBAL_PEOPLE_SEARCH_INDEX_NAME;

export type ActiveCallPersonSearchResult = {
  people: PersonHit[];
  directPersonMatch?: PersonHit;
};

type SearchableIndex = Pick<SearchIndex, "search">;
const FACILITY_EMPLOYEE_HITS_PER_PAGE = 50;

export const createActiveCallSearchIndex = (): SearchIndex => {
  return algoliasearch(appId, apiKey).initIndex(indexName);
};

const uniquePeople = (people: PersonHit[]): PersonHit[] => {
  const seen = new Set<string>();

  return people.filter((person) => {
    const key = person.pga_id || person.legacy_id || person.objectID;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const searchPeopleForFacility = async (index: SearchableIndex, facilityId: string): Promise<PersonHit[]> => {
  const results = await index.search<PersonHit>(facilityId, {
    hitsPerPage: FACILITY_EMPLOYEE_HITS_PER_PAGE,
    filters: "object_type:Person",
    restrictSearchableAttributes: ["facility_id"]
  });

  return results.hits.filter((person) => person.facility_id === facilityId);
};

const addFacilityNameToPeople = (people: PersonHit[], facility: FacilityHit): PersonHit[] => {
  return people.map((person) => ({
    ...person,
    facility_name: person.facility_name || facility.facility_name || facility.name
  }));
};

export const searchPeopleForQuery = async (index: SearchableIndex, query: string): Promise<PersonHit[]> => {
  const results = await index.search<PersonHit>(normalizePhoneSearchQuery(query), {
    hitsPerPage: 25,
    filters: "object_type:Person",
    restrictSearchableAttributes: ["name", "preferred_name", "pga_id", "phone", "phones", "legacy_id", "email"]
  });

  return results.hits;
};

export const searchPeopleForPhone = async (
  index: SearchableIndex,
  phone: string
): Promise<ActiveCallPersonSearchResult> => {
  const normalizedPhone = normalizePhoneNumber(phone);
  const phoneQueries = Array.from(
    new Set([normalizedPhone, formatPhoneQueryWithDashes(phone), formatPhoneNumber(phone)])
  );
  let phoneMatchedPeople: PersonHit[] = [];
  let phoneMatchedFacilities: FacilityHit[] = [];

  for (const phoneQuery of phoneQueries) {
    const results = await index.search<GlobalHit>(phoneQuery, {
      hitsPerPage: 25,
      filters: "object_type:Person OR object_type:Facility",
      restrictSearchableAttributes: [
        "name",
        "preferred_name",
        "facility_name",
        "pga_id",
        "phone",
        "phones",
        "legacy_id"
      ]
    });

    const globalHits = results.hits;
    phoneMatchedPeople = globalHits.filter((hit): hit is PersonHit => {
      return hit.object_type === "Person" && hitHasPhoneNumber(hit, phone);
    });
    phoneMatchedFacilities = globalHits.filter((hit): hit is FacilityHit => {
      return hit.object_type === "Facility" && hitHasPhoneNumber(hit, phone);
    });

    if (phoneMatchedPeople.length > 0 || phoneMatchedFacilities.length > 0) break;
  }

  const facilityEmployees = (
    await Promise.all(
      phoneMatchedFacilities
        .filter((facility) => !!facility.facility_id)
        .map(async (facility) =>
          addFacilityNameToPeople(await searchPeopleForFacility(index, facility.facility_id), facility)
        )
    )
  ).flat();
  const people = uniquePeople([...phoneMatchedPeople, ...facilityEmployees]);
  const directPersonMatch =
    phoneMatchedPeople.length === 1 && phoneMatchedFacilities.length === 0 && people.length === 1
      ? phoneMatchedPeople[0]
      : undefined;

  return { people, directPersonMatch };
};
